import * as React from "react";

/**
 * A hook that provides a deferred value using React's transition system. This is compatible with React Router v7's
 * internal use of useTransition and avoids conflicts that can occur with useDeferredValue + Suspense.
 *
 * This hook is most commonly used when URL search params change and need to be passed to a useSuspenseQuery. The
 * typical pattern is:
 *
 * 1. URL search params change
 * 2. Query parameters are derived and memoized
 * 3. UseDeferredTransition defers the params update
 * 4. IsPending is used to show loading UI (opacity, cursor states)
 * 5. DeferredValue is passed to useSuspenseQuery
 *
 * This prevents UI the suspense boundary from showing when search/filter parameters change.
 *
 * @param value The value to defer
 * @returns A tuple containing [isPending, deferredValue]
 */
export function useDeferredTransition<T>(value: T): [boolean, T] {
  const [deferredValue, setDeferredValue] = React.useState(value);
  const [isPending, startTransition] = React.useTransition();
  const prevValueRef = React.useRef(value);

  React.useEffect(() => {
    // only start transition if value actually changed
    if (JSON.stringify(value) !== JSON.stringify(prevValueRef.current)) {
      prevValueRef.current = value;
      startTransition(() => {
        setDeferredValue(value);
      });
    }
  }, [value]);

  return [isPending, deferredValue] as const;
}
